import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { inject, observer } from 'mobx-react';
import { compose } from 'utils/compose';

// Matches the server-side per-(shipment, IP) rate-limit cap on /sms-opt-in/verify (5 attempts
// in a 30-min window). The server also enforces a per-IP cumulative cap of 10 attempts across
// all shipments — that cap is intentionally not surfaced in the modal because it's a cross-
// shipment abuse signal, not a per-flow lockout. Users who hit the per-IP cap will see a 429
// response which surfaces via the toast error handler in index.jsx.
export const MAX_ATTEMPTS = 5;

const FIELD_LABELS = {
  order_number: 'Order Number',
  internal_id: 'Reference Number',
  delivery_items: 'Delivery Items',
  zip: 'Delivery ZIP Code',
  house_number: 'House Number',
};

// Fields whose values are predominantly digits — show a numeric keypad on mobile.
// House numbers can include alphanumerics like "12B", but the common case is digits;
// users can switch keyboards if needed.
const NUMERIC_INPUT_FIELDS = new Set(['zip', 'house_number']);

function toTitleCase(str) {
  return str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function resolveFieldLabel(field) {
  if (!field) return 'Reference';
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  if (field.startsWith('extra.')) return toTitleCase(field.slice('extra.'.length));
  return toTitleCase(field);
}

function SmsVerificationModal({ open, onClose, onSuccess, store, attempts, onAttemptsChange, settings }) {
  const { deliveryStore } = store;
  const [referenceInput, setReferenceInput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const isLocked = attempts >= MAX_ATTEMPTS;
  const fieldLabel = resolveFieldLabel(settings?.sms_opt_in_verification_field);

  useEffect(() => {
    if (open) {
      setReferenceInput('');
      setError('');
    }
  }, [open]);

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      await deliveryStore.verifySmsOptIn(referenceInput.trim());
      await onSuccess();
    } catch (e) {
      if (e?.response?.status === 429) {
        onAttemptsChange(MAX_ATTEMPTS);
        setError('');
      } else if (e?.response?.status === 400) {
        const nextAttempts = attempts + 1;
        onAttemptsChange(nextAttempts);
        setReferenceInput('');
        if (nextAttempts >= MAX_ATTEMPTS) {
          setError('');
        } else {
          setError('The reference you entered does not match. Please try again.');
        }
      } else {
        setError('Unable to verify. Please check your connection and try again.');
      }
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleClose = () => {
    setReferenceInput('');
    setError('');
    setLoading(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth aria-labelledby="sms-verify-dialog-title">
      <DialogTitle id="sms-verify-dialog-title">Verify Your Identity</DialogTitle>
      <DialogContent>
        {isLocked ? (
          <Box py={1}>
            <Typography variant="body2" color="error" gutterBottom role="alert">
              Too many incorrect attempts. Please contact support for assistance.
            </Typography>
          </Box>
        ) : (
          <Box py={1}>
            <Typography variant="body2" gutterBottom>
              Please enter your {fieldLabel} to confirm your identity.
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label={fieldLabel}
              value={referenceInput}
              onChange={(e) => setReferenceInput(e.target.value)}
              error={!!error}
              helperText={error}
              disabled={loading}
              onKeyDown={(e) => e.key === 'Enter' && referenceInput.trim() && !isLocked && !loading && handleSubmit()}
              slotProps={{
                htmlInput: {
                  'aria-label': fieldLabel,
                  'aria-describedby': error ? 'verify-error' : undefined,
                  ...(NUMERIC_INPUT_FIELDS.has(settings?.sms_opt_in_verification_field) && { inputMode: 'numeric' }),
                },
                formHelperText: { id: 'verify-error', role: error ? 'alert' : undefined },
              }}
              sx={{ mt: 1 }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button onClick={handleClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        {!isLocked && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disableElevation
            disabled={!referenceInput.trim() || loading}
            sx={{ borderRadius: '40px', textTransform: 'initial', fontWeight: 600, minWidth: 100 }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Verify'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default compose(inject('store'), observer)(SmsVerificationModal);
